import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      // Check if admin exists
      try {
        const setupCheck = await axios.get(`${API_URL}/api/auth/check-setup`);
        setNeedsSetup(!setupCheck.data.admin_exists);
      } catch (error) {
        console.error('Setup check failed:', error);
      }

      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const setupAdmin = async (username, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/setup`, {
      username,
      password,
      name
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setNeedsSetup(false);
    return userData;
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      username,
      password
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Try to log the logout action
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Ignore errors on logout
    }
    
    // Clear impersonation data if any
    localStorage.removeItem('original_admin_token');
    localStorage.removeItem('original_admin_user');
    localStorage.removeItem('is_impersonating');
    localStorage.removeItem('impersonated_bakery_name');
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Return to original super admin account
  const stopImpersonation = async () => {
    // Check for super admin impersonation first
    const originalSuperAdminToken = localStorage.getItem('original_super_admin_token');
    if (originalSuperAdminToken) {
      // Restore super admin token
      localStorage.setItem('token', originalSuperAdminToken);
      setToken(originalSuperAdminToken);
      
      // Clear impersonation data
      localStorage.removeItem('original_super_admin_token');
      localStorage.removeItem('impersonated_bakery_name');
      
      // Reload to refresh user data
      window.location.href = '/settings';
      return true;
    }
    
    // Check for regular admin impersonation
    const originalToken = localStorage.getItem('original_admin_token');
    const originalUser = localStorage.getItem('original_admin_user');
    
    if (!originalToken || !originalUser) {
      return false;
    }
    
    // Restore original admin
    localStorage.setItem('token', originalToken);
    setToken(originalToken);
    setUser(JSON.parse(originalUser));
    
    // Clear impersonation data
    localStorage.removeItem('original_admin_token');
    localStorage.removeItem('original_admin_user');
    localStorage.removeItem('is_impersonating');
    localStorage.removeItem('impersonated_bakery_name');
    
    return true;
  };

  // Check if currently impersonating (either super admin or regular admin)
  const isSuperAdminImpersonating = !!localStorage.getItem('original_super_admin_token');
  const isImpersonating = localStorage.getItem('is_impersonating') === 'true' || isSuperAdminImpersonating;
  const impersonatedBakeryName = localStorage.getItem('impersonated_bakery_name');

  const getAuthHeader = () => ({
    Authorization: `Bearer ${token}`
  });

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin has all permissions
    if (user.role === 'staff' && user.permissions) {
      return user.permissions[permission] === true;
    }
    return false;
  };

  const isAdmin = user?.role === 'admin';
  const isDriver = user?.role === 'driver';
  const isStaff = user?.role === 'staff';

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        token,
        setToken,
        loading,
        needsSetup,
        isAuthenticated: !!user,
        isAdmin,
        isDriver,
        isStaff,
        isImpersonating,
        impersonatedBakeryName,
        hasPermission,
        setupAdmin,
        login,
        logout,
        stopImpersonation,
        getAuthHeader,
        API_URL
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
